/**
 * Test Data Generator - Creates random NPCs, missions, and other data during boot-up
 */

import { DATABASE_SERVICE_URL } from './workerUrls';
import { logService } from './logService';

// Random name generators
const firstNames = [
  'Zara', 'Marcus', 'Elena', 'Viktor', 'Nova', 'Rex', 'Luna', 'Jax',
  'Iris', 'Kane', 'Vera', 'Dex', 'Mira', 'Cade', 'Nyx', 'Roan',
  'Sage', 'Blaze', 'Storm', 'Echo', 'Dash', 'Flint', 'Raven', 'Orion',
];

const lastNames = [
  'Vega', 'Steel', 'Frost', 'Cross', 'Drake', 'Stone', 'Black', 'Grey',
  'Wolf', 'Fox', 'Hart', 'Nash', 'Cole', 'Reed', 'Shaw', 'Ward',
  'Hawk', 'Nova', 'Blaze', 'Storm', 'Shade', 'Void', 'Star', 'Moon',
];

const npcTitles = [
  'Captain', 'Commander', 'Lieutenant', 'Sergeant', 'Pilot', 'Trader',
  'Merchant', 'Smuggler', 'Bounty Hunter', 'Mercenary', 'Prospector',
  'Engineer', 'Scientist', 'Diplomat', 'Agent', 'Fixer', 'Broker',
];

const npcTypes = [
  'trader', 'pirate', 'bounty_hunter', 'patrol', 'quest_giver',
  'civilian', 'mercenary', 'smuggler',
];

const missionTypes = [
  'delivery', 'smuggle', 'assassination', 'bounty', 'rescue',
  'mining', 'exploration', 'passenger', 'data_courier',
];

const missionTitles = {
  delivery: ['Urgent Delivery', 'Medical Supplies Transport', 'Critical Parts Shipment', 'VIP Package'],
  smuggle: ['Discreet Cargo', 'Under the Radar', 'No Questions Asked', 'Quiet Transport'],
  assassination: ['Eliminate the Target', 'Clean Removal', 'Silent Takedown', 'Final Contract'],
  bounty: ['Wanted Dead or Alive', 'Capture the Fugitive', 'Hunt the Pirate', 'Bring to Justice'],
  rescue: ['Emergency Rescue', 'Search and Recovery', 'Save the Crew', 'Distress Signal'],
  mining: ['Ore Extraction', 'Asteroid Harvest', 'Deep Core Mining', 'Mineral Survey'],
  exploration: ['Unknown Sector Survey', 'Chart New Routes', 'First Contact', 'System Scan'],
  passenger: ['VIP Transport', 'Refugee Evacuation', 'Tourist Charter', 'Witness Protection'],
  data_courier: ['Encrypted Data Run', 'Black Box Recovery', 'Intel Delivery', 'Secure Transfer'],
};

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomNumber(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateNPCName(): string {
  return `${randomItem(firstNames)} ${randomItem(lastNames)}`;
}

function generateNPCTitle(): string {
  return randomItem(npcTitles);
}

async function postApi<T>(endpoint: string, data: Record<string, unknown>): Promise<T | null> {
  const startTime = Date.now();
  const logId = logService.api('POST', endpoint, data, 'pending');

  try {
    const response = await fetch(`${DATABASE_SERVICE_URL()}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      logService.updateLog(logId, {
        status: 'error',
        duration,
        message: `POST ${endpoint} - ${response.status}`,
      });
      return null;
    }

    const json = await response.json();
    logService.updateLog(logId, {
      status: json.success ? 'success' : 'error',
      duration,
      details: json.data ? { id: json.data.id } : { error: json.error },
    });
    return json.data ?? null;
  } catch (error) {
    const duration = Date.now() - startTime;
    logService.updateLog(logId, {
      status: 'error',
      duration,
      message: `POST ${endpoint} - ${error instanceof Error ? error.message : 'Unknown error'}`,
    });
    return null;
  }
}

async function getApi<T>(endpoint: string): Promise<T | null> {
  try {
    const response = await fetch(`${DATABASE_SERVICE_URL()}${endpoint}`);
    if (!response.ok) return null;
    const json = await response.json();
    return json.success ? json.data : null;
  } catch {
    return null;
  }
}

async function putR2(bucket: string, key: string, content: string, contentType = 'text/plain'): Promise<boolean> {
  const startTime = Date.now();
  const logId = logService.api('PUT', `/api/r2/${bucket}/object/${key}`, { size: content.length }, 'pending');

  try {
    const response = await fetch(`${DATABASE_SERVICE_URL()}/api/r2/${bucket}/object/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: content,
    });
    const duration = Date.now() - startTime;

    if (!response.ok) {
      logService.updateLog(logId, { status: 'error', duration, message: `PUT failed: ${response.status}` });
      return false;
    }

    logService.updateLog(logId, { status: 'success', duration, details: { key, bucket } });
    return true;
  } catch (error) {
    const duration = Date.now() - startTime;
    logService.updateLog(logId, {
      status: 'error',
      duration,
      message: `PUT R2 - ${error instanceof Error ? error.message : 'Unknown error'}`,
    });
    return false;
  }
}

async function getR2Objects(bucket: string): Promise<{ key: string }[] | null> {
  try {
    const response = await fetch(`${DATABASE_SERVICE_URL()}/api/r2/${bucket}/objects`);
    if (!response.ok) return null;
    const json = await response.json();
    return json.success ? json.data.objects : null;
  } catch {
    return null;
  }
}

// Common commodities that should be available at most stations
const commonCommodities = [
  { id: 'com-water', basePrice: 50, volatility: 0.1 },
  { id: 'com-food', basePrice: 75, volatility: 0.15 },
  { id: 'com-ore', basePrice: 150, volatility: 0.2 },
  { id: 'com-metals', basePrice: 300, volatility: 0.15 },
  { id: 'com-machinery', basePrice: 500, volatility: 0.1 },
  { id: 'com-components', basePrice: 1000, volatility: 0.15 },
  { id: 'com-textiles', basePrice: 100, volatility: 0.1 },
  { id: 'com-spices', basePrice: 800, volatility: 0.3 },
  { id: 'com-rusty-spanners', basePrice: 25, volatility: 0.1 },
  { id: 'com-tea', basePrice: 400, volatility: 0.2 },
];

const supplyLevels = ['none', 'low', 'medium', 'high'] as const;
const demandLevels = ['none', 'low', 'medium', 'high'] as const;

async function generateMarketForStation(stationId: string): Promise<void> {
  logService.info('db+init', `Generating market data for station ${stationId}...`);

  // Check if station already has market data
  const existingMarket = await getApi<Array<{ commodity_id: string }>>(`/api/stations/${stationId}/market`);
  if (existingMarket && existingMarket.length > 0) {
    logService.info('db+init', `Station already has ${existingMarket.length} market entries, skipping`);
    return;
  }

  // Generate market entries for random subset of commodities
  const commoditiesToAdd = commonCommodities.filter(() => Math.random() > 0.3); // ~70% chance for each

  for (const commodity of commoditiesToAdd) {
    // Randomize price based on base price and volatility
    const priceVariation = 1 + (Math.random() - 0.5) * 2 * commodity.volatility;
    const buyPrice = Math.round(commodity.basePrice * priceVariation);
    const sellPrice = Math.round(buyPrice * (0.85 + Math.random() * 0.1)); // Sell price is 85-95% of buy

    const marketEntry = {
      commodity_id: commodity.id,
      buy_price: buyPrice,
      sell_price: sellPrice,
      supply: randomNumber(0, 1000),
      demand: randomNumber(0, 1000),
      supply_level: randomItem([...supplyLevels]),
      demand_level: randomItem([...demandLevels]),
      is_available: true,
    };

    await postApi(`/api/stations/${stationId}/market`, marketEntry);
  }

  logService.info('db+init', `Created ${commoditiesToAdd.length} market entries for station`);
}

async function addStartingCargo(playerId: string): Promise<void> {
  logService.info('db+init', 'Checking player cargo...');

  // Get player's ships
  const ships = await getApi<Array<{ id: string; name: string }>>(`/api/players/${playerId}/ships`);
  if (!ships || ships.length === 0) {
    logService.warning('db+init', 'No ships found for player, skipping cargo');
    return;
  }

  const shipId = ships[0].id;

  // Check if ship already has cargo
  const existingCargo = await getApi<Array<{ commodity_id: string }>>(`/api/ships/${shipId}/cargo`);
  if (existingCargo && existingCargo.length > 0) {
    logService.info('db+init', `Ship already has ${existingCargo.length} cargo items, skipping`);
    return;
  }

  // Add some rusty spanners to the cargo hold
  logService.info('db+init', 'Adding starting cargo: Rusty Spanners');
  await postApi(`/api/ships/${shipId}/cargo`, {
    commodity_id: 'com-rusty-spanners',
    quantity: 5,
    purchase_price: 20,
  });
}

export async function generateTestData(playerId: string): Promise<void> {
  logService.info('db+init', 'Starting test data generation...');

  try {
    // Add starting cargo to player's ship (rusty spanners!)
    await addStartingCargo(playerId);

    // Generate market data for the starting station
    const startingStationId = 'sta-nc-central';
    await generateMarketForStation(startingStationId);

    // Get existing systems and stations for random placement
    const systems = await getApi<Array<{ id: string; name: string }>>('/api/systems');
    const stations = await getApi<Array<{ id: string; name: string; system_id: string }>>('/api/stations');

    if (!systems || systems.length === 0) {
      logService.warning('db+init', 'No systems available, skipping NPC generation');
      return;
    }

    // Generate a few random NPCs at different locations
    const npcCount = randomNumber(2, 4);
    logService.info('db+init', `Generating ${npcCount} random NPCs...`);

    for (let i = 0; i < npcCount; i++) {
      const npcType = randomItem(npcTypes);
      const system = randomItem(systems);
      const stationsInSystem = stations?.filter(s => s.system_id === system.id) || [];
      const station = stationsInSystem.length > 0 ? randomItem(stationsInSystem) : null;

      const npcData = {
        name: generateNPCName(),
        title: generateNPCTitle(),
        npc_type: npcType,
        home_system_id: system.id,
        current_system_id: system.id,
        current_station_id: station?.id || null,
        aggression: npcType === 'pirate' ? randomNumber(7, 10) : randomNumber(1, 5),  // DB constraint: 0-10
        skill_level: randomNumber(3, 9),  // DB constraint: 1-10
      };

      await postApi('/api/npcs', npcData);
    }

    // Generate a couple of random missions at the player's starting station
    const missionCount = randomNumber(1, 3);
    logService.info('db+init', `Generating ${missionCount} random missions...`);

    for (let i = 0; i < missionCount; i++) {
      const missionType = randomItem(missionTypes) as keyof typeof missionTitles;
      const titles = missionTitles[missionType] || ['Unknown Mission'];
      const destSystem = randomItem(systems);
      const difficulty = randomNumber(1, 5);

      const missionData = {
        issuing_station_id: startingStationId,
        mission_type: missionType,
        title: randomItem(titles),
        description: `A ${missionType} mission requiring travel to ${destSystem.name}.`,
        destination_system_id: destSystem.id,
        reward_credits: randomNumber(500, 5000) * difficulty,
        reward_reputation: randomNumber(5, 25),
        time_limit_hours: randomNumber(12, 72),
        difficulty,  // DB column: 1-5 (Trivial, Easy, Moderate, Hard, Extreme)
      };

      await postApi('/api/missions', missionData);
    }

    logService.info('db+init', 'Test data generation complete');

    // Seed R2 lore content if empty
    await seedR2Content();
  } catch (error) {
    logService.error('db+init', `Error generating test data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

async function seedR2Content(): Promise<void> {
  logService.info('db+R2', 'Checking R2 lore bucket...');

  // Check if lore bucket already has content
  const existingObjects = await getR2Objects('lore');
  if (existingObjects && existingObjects.length > 0) {
    logService.info('db+R2', `Lore bucket already has ${existingObjects.length} objects, skipping seed`);
    return;
  }

  logService.info('db+R2', 'Seeding R2 lore content...');

  // Seed some initial lore content
  const loreContent = [
    {
      key: 'factions/earth-remnant.md',
      content: `# Earth Remnant

## Overview
The Earth Remnant represents what remains of humanity's original government after the Great Expansion. Based in the Sol system, they maintain a rigid bureaucracy and claim authority over all human space.

## History
When humanity first developed jump drive technology in 2187, Earth's government tried to maintain control of all colonization efforts. The Great Expansion saw billions leave for new worlds, and Earth's grip weakened considerably.

## Current Status
Today, the Earth Remnant controls a handful of core systems but their influence wanes further each decade. They are known for excessive taxation and regulation.

## Player Relations
New traders often find Earth Remnant space safe but unprofitable. Their strict cargo inspections make smuggling nearly impossible.`,
      type: 'text/markdown',
    },
    {
      key: 'factions/galactic-admin.md',
      content: `# Galactic Administration

## Overview
The Galactic Administration emerged as a neutral governing body to facilitate trade and prevent interstellar war. They maintain jump lane beacons and operate most major trading stations.

## Services
- Jump lane beacon maintenance
- Trade dispute arbitration
- Station security contracts
- Pilot licensing

## Reputation
Working with the Galactic Admin improves your standing at most stations and unlocks access to premium docking bays and trade deals.`,
      type: 'text/markdown',
    },
    {
      key: 'locations/new-croydon.md',
      content: `# New Croydon System

## Overview
New Croydon serves as the starting location for most independent pilots. The system offers a mix of opportunities without the heavy taxation of Earth space.

## Notable Locations
- **New Croydon Central Station**: The main trading hub, offering basic supplies and ship repairs
- **Croydon Mining Platform**: Asteroid mining operation buying raw ore
- **Freeport Haven**: A less regulated station known for "flexible" trade practices

## Economy
The system's economy runs on mining and trade. Ore from the asteroid belt feeds manufacturing in neighboring systems.`,
      type: 'text/markdown',
    },
    {
      key: 'ships/sidestep.md',
      content: `# Sidestep-class Light Freighter

## Specifications
- **Class**: Light Freighter
- **Cargo Capacity**: 50 units
- **Jump Range**: 15 LY
- **Hardpoints**: 1 small

## Overview
The Sidestep is the quintessential starter ship. Cheap to buy, cheap to run, and reliable enough to get you from A to B. Don't expect to win any races or battles.

## Upgrades
Common upgrades include cargo expansion modules and fuel tank extensions. Some pilots add mining lasers for asteroid prospecting.`,
      type: 'text/markdown',
    },
    {
      key: 'guides/getting-started.md',
      content: `# Getting Started Guide

## Welcome, Pilot!

You've just acquired your first ship and are ready to make your mark on the galaxy. Here's what you need to know:

### Basic Trading
1. Check the station market for commodity prices
2. Buy low in systems that produce goods
3. Sell high in systems that consume goods
4. Watch for trade route opportunities

### Fuel Management
Your ship's fuel is precious. Plan your jumps carefully and always keep enough reserve to reach the nearest station.

### Reputation
Your actions affect how factions view you. Help traders to gain standing with merchant guilds. Attack pirates to gain military favor.

### First Steps
1. Check available missions at New Croydon Central
2. Take a simple delivery mission to learn the ropes
3. Explore neighboring systems
4. Build up credits for ship upgrades

Good luck out there!`,
      type: 'text/markdown',
    },
  ];

  for (const item of loreContent) {
    const success = await putR2('lore', item.key, item.content, item.type);
    if (success) {
      logService.info('db+R2', `Seeded lore: ${item.key}`);
    }
  }

  logService.info('db+R2', 'R2 lore seeding complete');
}

// Hook to run test data generation once on startup
let hasGenerated = false;

export function initTestData(playerId: string): void {
  if (hasGenerated) return;
  hasGenerated = true;

  // Run test data generation after a short delay to let the app initialize
  setTimeout(() => {
    generateTestData(playerId);
  }, 2000);
}
